import { Injectable, Injector } from "@angular/core";
import { ActionResultDto, FlowClient, FlowRenderInfoOutputDto, GuestTaskRenderInfoOutputDto, GuestTaskRenderInfoOutputDtoEntityActionResultDto, RespondTaskAsGuestInputDto, RespondTaskInputDto, SaveFlowInputDto, StartFlowAsGuestInputDto, StartFlowInputDto, StartFlowRenderInfoOutputDto, StartFlowResultOutputDto, TaskClient, TaskRenderInfoOutputDto, TaskRenderInfoOutputDtoEntityActionResultDto, TaskResponseResult } from "../../core/remote-clients/remote-client";
import { firstValueFrom, Observable } from "rxjs";
import { RemoteResponse, RemoteServiceBase } from "./remote-base.service";

@Injectable()
export class RemoteFlowService extends RemoteServiceBase {
    protected readonly _flowClient: FlowClient;
    protected readonly _taskClient: TaskClient;

    constructor(injector: Injector) {
        super(injector);
        this._flowClient = injector.get(FlowClient);
        this._taskClient = injector.get(TaskClient);
    }

    public async getNewFlowInfo(templateId: string): Promise<StartFlowRenderInfoOutputDto> {
        const request = this.isGuest
            ? this._flowClient.newGuestFlowInfo(templateId)
            : this._flowClient.newFlowInfo(templateId);
        const result = await firstValueFrom(request);

        this.validateResponse(result as RemoteResponse);

        return result.entity;
    }

    public async getFlowRenderInfo(flowId: string, formType: number): Promise<FlowRenderInfoOutputDto> {
        const result = await firstValueFrom(this._flowClient.renderInfo(flowId, formType));

        this.validateResponse(result as RemoteResponse);

        return result.entity;
    }

    public async start(input: StartFlowInputDto | StartFlowAsGuestInputDto): Promise<StartFlowResultOutputDto> {
        const request = this.isGuest ?
            this._flowClient.startGuest(input as StartFlowAsGuestInputDto) :
            this._flowClient.start(input as StartFlowInputDto);

        const result = await firstValueFrom(request);

        this.validateResponse(result as RemoteResponse);

        return result.entity;
    }

    public async respondTask(respondTaskDto: RespondTaskInputDto | RespondTaskAsGuestInputDto): Promise<TaskResponseResult> {
        const request = this.isGuest ?
            this._taskClient.respondGuestTask(respondTaskDto as RespondTaskAsGuestInputDto) :
            this._taskClient.respondTask(respondTaskDto as RespondTaskInputDto);

        const result = await firstValueFrom(request);

        this.validateResponse(result as RemoteResponse);

        return result.entity;
    }

    public async getTaskRenderInfo(input: TaskRenderInfoInputDto | GuestTaskRenderInfoInputDto): Promise<TaskRenderInfoOutputDto | GuestTaskRenderInfoOutputDto> {
        let request: Observable<TaskRenderInfoOutputDtoEntityActionResultDto | GuestTaskRenderInfoOutputDtoEntityActionResultDto>;
        if (this.isGuest) {
            const guestInput = input as GuestTaskRenderInfoInputDto;
            request = this._taskClient.guestTaskRenderInfo(guestInput.token);
        } else {
            const taskInput = input as TaskRenderInfoInputDto;
            request = this._taskClient.taskRenderInfo(taskInput.flowId, taskInput.taskId, taskInput.taskToId);
        }

        const result = await firstValueFrom(request);

        this.validateResponse(result as RemoteResponse);

        return result.entity;
    }

    public async saveFlow(saveFlowDto: SaveFlowInputDto): Promise<ActionResultDto> {
        const result = (await firstValueFrom(
            this._flowClient.edit(saveFlowDto)
        )) as ActionResultDto;

        if (result.success === false) {
            throw new Error(result.message ?? "UnexpectedErrorEditingFlow");
        }

        return result;
    }
}

export interface TaskRenderInfoInputDto {
    flowId: string;
    taskId: string;
    taskToId: string;
    tenantId: string;
}

export interface GuestTaskRenderInfoInputDto {
    token: string;
    tenantId: string;
}